













































































import { COLOR_BASE_DIALOG_BACKGROUND, COLOR_BASE_DIALOG_BACKGROUND_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { nextUpdateHoldId, setUpdateHold } from "@/utils/UpdateHold";

interface BaseDialogOptions {
  title: string;
  positiveButtonText?: string | null;
  negativeButtonText?: string | null;
  neutralButtonText?: string | null;
  neutralPositiveButtonText?: string | null;
  additionalNeutralPositiveButtonText?: string | null;
  scrollable?: boolean;
  maxWidth?: number;
  closeOnSave?: boolean;
  closeOnNeutralAction?: boolean;
  closeOnNeutralPositiveAction?: boolean;
  closeOnAdditionalNeutralPositiveAction?: boolean;
  persistent?: boolean;
  styleAsAlertDialog?: boolean;
  hidePositiveButtonIfEmpty?: boolean;
  hideSaveButton?: boolean;
}

@Component
export default class BaseDialogComponent extends Vue {
  isShowing = false;
  hidePositiveButtonIfEmpty = false;

  @Prop({ default: false })
  saveDisabled!: boolean;

  @Prop({ default: false })
  shouldUseInfiniteScrolling!: boolean;

  @Prop({ default: false })
  cancelDisabled!: boolean;

  /** Disables the button left of the positive one, as `saveDisabled` does the positive one. */
  @Prop({ default: false })
  neutralPositiveDisabled!: boolean;

  @Prop({ default: false })
  loading!: boolean;

  @Prop({ default: false })
  sharedEmailAddressOptions!: boolean;

  @Prop({ default: false })
  preventClosingDialog!: boolean;

  @Watch("isShowing")
  onIsShowingChanged(newValue: boolean) {
    if (newValue) {
      this.$emit("onDialogOpened");
    }
    else if (newValue === false) {
      this.$emit("onDialogClosed");
    }
  }

  /**
   * Non-reactive hold key. Per instance, because dialogs nest: a shared key would let an inner
   * dialog closing release the hold an outer dialog still needs.
   */
  private appUpdateHoldId!: string;

  /**
   * True while this dialog is open in a state a reload must not interrupt.
   *
   * `persistent` is the repo's edit-mode signal for dialogs (see
   * .claude/rules/edit-mode-dialogs.md: view mode closes on an outside click, edit mode does not),
   * so an open persistent dialog means unsaved edits are on screen. It also covers the dialogs that
   * are persistent because they run an action to completion, and in either case a banner behind the
   * modal overlay would be unreachable anyway.
   */
  get holdsAppUpdate(): boolean {
    return this.isShowing && this.persistent;
  }

  @Watch("holdsAppUpdate")
  onHoldsAppUpdateChanged(holdsAppUpdate: boolean) {
    if (!this.appUpdateHoldId) {
      this.appUpdateHoldId = nextUpdateHoldId("base-dialog");
    }

    setUpdateHold(this.appUpdateHoldId, holdsAppUpdate);
  }

  /**
   * `beforeDestroy` does not fire for a component inside `<keep-alive>` (Scaffold keeps several list
   * screens alive), so the hold is released on deactivation as well and re-derived on return. A
   * hold left behind would silence the banner for the rest of the session.
   */
  deactivated() {
    this.releaseAppUpdateHold();
  }

  activated() {
    this.onHoldsAppUpdateChanged(this.holdsAppUpdate);
  }

  beforeDestroy() {
    this.releaseAppUpdateHold();
  }

  private releaseAppUpdateHold() {
    if (this.appUpdateHoldId) {
      setUpdateHold(this.appUpdateHoldId, false);
    }
  }

  @Watch("positiveButtonText")
  onPositiveButtonTextChanged(newValue: string | null) {
    if (newValue) {
      if (newValue === "" && this.hidePositiveButtonIfEmpty) {
        this.showPositiveButton = false;
      }
      else {
        this.showPositiveButton = true;
      }
    }
    else {
      this.showPositiveButton = false;
    }
  }

  @Watch("neutralPositiveButtonText")
  onNeutralPositiveButtonText(newValue: string | null) {
    if (newValue) {
      if (newValue === "" && this.hidePositiveButtonIfEmpty) {
        this.showNeutralPositiveButton = false;
      }
      else {
        this.showNeutralPositiveButton = true;
      }
    }
    else {
      this.showNeutralPositiveButton = false;
    }
  }

  @Watch("additionalNeutralPositiveButtonText")
  onAdditionalNeutralPositiveButtonText(newValue: string | null) {
    if (newValue) {
      if (newValue === "" && this.hidePositiveButtonIfEmpty) {
        this.showAdditionalNeutralPositiveButton = false;
      }
      else {
        this.showAdditionalNeutralPositiveButton = true;
      }
    }
    else {
      this.showAdditionalNeutralPositiveButton = false;
    }
  }

  title = "";
  positiveButtonText = "";
  negativeButtonText = "";
  neutralButtonText = "";
  neutralPositiveButtonText = "";
  additionalNeutralPositiveButtonText = "";
  maxWidth = 600;

  /**
   * Phones (below 600 px) show a form dialog full screen, as Apple and Material do; alert-style and
   * small dialogs (400 px or less) stay floating. Scrollable then too, so the buttons stay in view.
   */
  get isPhoneFullscreen() {
    return this.$vuetify.breakpoint.xsOnly && !this.styleAsAlertDialog && this.maxWidth > 400;
  }

  styleAsAlertDialog = false;

  scrollable = true;

  closeOnSave = true;
  closeOnNeutralAction = true;
  closeOnNeutralPositiveAction = true;
  closeOnAdditionalNeutralPositiveAction = true;

  persistent = false;

  showPositiveButton = true;
  showNegativeButton = true;
  showNeutralButton = true;
  showNeutralPositiveButton = true;
  showAdditionalNeutralPositiveButton = true;

  hideSaveButton = false;

  show(options: BaseDialogOptions) {
    this.isShowing = true;
    this.hidePositiveButtonIfEmpty = options.hidePositiveButtonIfEmpty ?? false;
    this.hideSaveButton = options.hideSaveButton ?? false;

    this.styleAsAlertDialog = options.styleAsAlertDialog ?? false;
    this.title = options.title;

    this.maxWidth = options.maxWidth ?? 600;
    this.scrollable = options.scrollable ?? false;

    this.showPositiveButton = options.positiveButtonText !== null &&
      (!this.hidePositiveButtonIfEmpty ||
        (this.hidePositiveButtonIfEmpty && options.positiveButtonText !== ""));

    this.showNegativeButton = options.negativeButtonText !== null;
    this.showNeutralButton = !!options.neutralButtonText;
    this.showNeutralPositiveButton = !!options.neutralPositiveButtonText;
    this.showAdditionalNeutralPositiveButton = !!options.additionalNeutralPositiveButtonText;

    // No button stores an empty text: the positiveButtonText watcher runs after show() returns and
    // would otherwise turn the button back on when "" became "Sichern" (the first dialog opened).
    this.positiveButtonText =
      options.positiveButtonText === null
        ? ""
        : options.positiveButtonText
        ? options.positiveButtonText
        : this.hideSaveButton 
          ? "" 
          : this.$t("common.save").toString();
          
    this.negativeButtonText =
      options.negativeButtonText ?? this.$t("common.close").toString();

    this.neutralButtonText = options.neutralButtonText ?? "";
    this.neutralPositiveButtonText = options.neutralPositiveButtonText ?? "";
    this.additionalNeutralPositiveButtonText = options.additionalNeutralPositiveButtonText ?? "";

    this.persistent = options.persistent ?? false;

    this.closeOnNeutralAction = options.closeOnNeutralAction ?? true;
    this.closeOnNeutralPositiveAction = options.closeOnNeutralPositiveAction ?? true;
    this.closeOnAdditionalNeutralPositiveAction = options.closeOnAdditionalNeutralPositiveAction ?? true;
    this.closeOnSave = options.closeOnSave ?? true;
  }

  onPositiveAction() {
    if (this.closeOnSave) {
      this.isShowing = false;
    }

    this.$emit("positiveAction");
  }

  onNegativeAction() {
    if (!this.sharedEmailAddressOptions && !this.preventClosingDialog) {
      this.isShowing = false;
    }
    this.$emit("negativeAction");
  }

  onNeutralAction() {
    if (this.closeOnNeutralAction) {
      this.isShowing = false;
    }

    this.$emit("neutralAction");
  }

  onNeutralPositiveAction() {
    if (this.closeOnNeutralPositiveAction) {
      this.isShowing = false;
    }

    this.$emit("neutralPositiveAction");
  }

  onAdditionalNeutralPositiveAction() {
    if (this.closeOnAdditionalNeutralPositiveAction) {
      this.isShowing = false;
    }

    this.$emit("additionalNeutralPositiveAction");
  }

  onCancel() {
    this.$emit("cancel");
  }

  get style() {
    return this.styleAsAlertDialog
      ? ""
      : `background: ${themed(COLOR_BASE_DIALOG_BACKGROUND, COLOR_BASE_DIALOG_BACKGROUND_DARK)}`;
  }

  get shouldAddPaddingTop() {
    return this.title.length > 59;
  }
}
